import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { PawPrint, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MyPets() {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/pets').then(r => { setPets(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleDelete = async (petId, petName) => {
    if (!window.confirm(`Are you sure you want to remove ${petName}?`)) return;
    try {
      await api.delete(`/pets/${petId}`);
      setPets(pets.filter(p => p.pet_id !== petId));
      toast.success(`${petName} removed`);
    } catch {
      toast.error('Failed to remove pet');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="my-pets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>My Pets</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{pets.length} pet{pets.length !== 1 ? 's' : ''} registered</p>
        </div>
        <Button className="rounded-full bg-[#2D2D2D] text-white hover:bg-[#1A1A1A] shadow-md" onClick={() => navigate('/client/pets/new')} data-testid="add-pet-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Pet
        </Button>
      </div>

      {pets.length === 0 ? (
        <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white">
          <CardContent className="p-12 text-center">
            <PawPrint className="w-16 h-16 text-[#D6D6D0] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-2">No pets yet</h3>
            <p className="text-sm text-[#5A5A5A] mb-6 max-w-sm mx-auto">Add your furry friends to start booking grooming appointments.</p>
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => navigate('/client/pets/new')} data-testid="add-first-pet-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Pet
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pets.map((pet) => (
            <Card key={pet.pet_id} className="rounded-2xl border-[#E5E5E0] bg-white card-hover group" data-testid={`pet-card-${pet.pet_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-[#F5F5F0] rounded-2xl flex items-center justify-center">
                      {pet.photo_url ? (
                        <img src={pet.photo_url} alt={pet.name} className="w-full h-full object-cover rounded-2xl" />
                      ) : (
                        <PawPrint className="w-7 h-7 text-[#2D2D2D]" strokeWidth={1.5} />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#2D2D2D]">{pet.name}</h3>
                      <p className="text-sm text-[#5A5A5A]">{pet.breed || 'Unknown breed'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#888]">Species</span>
                    <span className="text-[#2D2D2D] font-medium capitalize">{pet.species}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#888]">Gender</span>
                    <span className="text-[#2D2D2D] font-medium">{pet.gender || 'N/A'}</span>
                  </div>
                  {pet.weight && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#888]">Weight</span>
                      <span className="text-[#2D2D2D] font-medium">{pet.weight} kg</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#888]">Spayed/Neutered</span>
                    <span className="text-[#2D2D2D] font-medium">{pet.spayed_neutered ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-xl border-[#E5E5E0] text-sm" onClick={() => navigate(`/client/pets/${pet.pet_id}`)} data-testid={`edit-pet-${pet.pet_id}`}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" className="rounded-xl border-[#E5E5E0] text-[#D9534F] hover:bg-red-50 hover:border-red-200" onClick={() => handleDelete(pet.pet_id, pet.name)} data-testid={`delete-pet-${pet.pet_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
